import React, { useEffect, useRef } from 'react';

import { CrossIcon, PlusIcon } from '@vlabs/icons';
import validate from '@vlabs/shared/validators';
import { Control, SettingsItemWrapper, Margin } from '@vlabs/uikit';
import cn from 'classnames';
import { useFormContext, useFieldArray, useWatch } from 'react-hook-form';

import { ControllableValueSelect } from '@vlabs/monokit/shared/ui/value-select';

import {
  LISTS_GROUP_BY_VALUE,
  timeGroupingTypeOptions,
  timeDimensionOptions,
  frequencyOptions,
  targetForGroupByAggregatorOptions,
} from '../constants';
import { useChartFormTranslation } from '../i18n';
import st from './shared.module.sass';
import type { ExtendedUseFormReturn } from '../types';

export function ClusteringForm(): React.JSX.Element {
  const { t } = useChartFormTranslation();
  const {
    groupByTypeValue,
    isPieOrDoughnutChartType,
    getValues,
    setValue,
    clearErrors,
    control,
    register,
    formState: { errors },
    resetState,
  } = useFormContext() as ExtendedUseFormReturn;
  const { fields, append, remove } = useFieldArray({ control, name: 'getEventStats.targets' });
  const previousGroupByTypeValueRef = useRef(groupByTypeValue);
  const watchedGroupByTargets = useWatch({ control, name: 'getEventStats.targets' }) ?? [];

  const isIntervalTypeSelected = groupByTypeValue === timeGroupingTypeOptions.interval.value;
  const isFrequencyTypeSelected = groupByTypeValue === timeGroupingTypeOptions.frequency.value;
  const hasListsGrouping: boolean = watchedGroupByTargets
    .some((target: { column: string; aggregator: string }) => target?.column === LISTS_GROUP_BY_VALUE);
  const hasTopMatchingGrouping: boolean = watchedGroupByTargets
    .some((target: { column: string; aggregator: string }) => target?.column === 'top_matching_candidates_label');

  useEffect(() => {
    if (resetState.current.clusteringForm) {
      resetState.current.clusteringForm = false;
      return;
    }
    if (previousGroupByTypeValueRef.current === groupByTypeValue) return;

    const fieldsToReset = [
      'getEventStats.group_by.frequency',
      'getEventStats.group_by.count',
      'getEventStats.group_by.timeDimension',
    ];
    fieldsToReset.forEach((field) => setValue(field, null));

    clearErrors('getEventStats.group_by');
    previousGroupByTypeValueRef.current = groupByTypeValue;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupByTypeValue, setValue, getValues]);

  const isGroupByDisabled = isPieOrDoughnutChartType || fields.length === 3;
  let groupByTimeTooltipContent = null;
  if (fields.length === 2) {
    groupByTimeTooltipContent = t('form.clustering.tooltip.two_fields');
  } else if (isPieOrDoughnutChartType) {
    groupByTimeTooltipContent = t('form.clustering.tooltip.pie_or_doughnut');
  }

  const conditions = {
    hasTwoFieldsWithoutGroupBy: !groupByTypeValue && fields.length === 3,
    hasOneFieldForGroupBy: (isPieOrDoughnutChartType || groupByTypeValue) && fields.length === 2,
  };
  const isAddFieldButtonDisabled = conditions.hasTwoFieldsWithoutGroupBy || conditions.hasOneFieldForGroupBy;
  const getIsOptionDisabled = (currentValue: string) => (option: string): boolean => {
    if (option === LISTS_GROUP_BY_VALUE) {
      return hasTopMatchingGrouping && currentValue !== LISTS_GROUP_BY_VALUE;
    }

    if (option === 'top_matching_candidates_label') {
      return hasListsGrouping && currentValue !== 'top_matching_candidates_label';
    }

    return false;
  };

  return (
    <React.Fragment>
      <div className={st.FormTitle}>{t('form.clustering.group_by_time')}</div>
      <div className={st.ColorPanel}>
        <SettingsItemWrapper
          contentClassName={st.SettingsItem__Content}
          data-tooltip-content={groupByTimeTooltipContent as unknown as string}
          disabled={isGroupByDisabled}
          title={t('form.clustering.group_by')}
          titleClassName={st.SettingsItem__Title}
        >
          <ControllableValueSelect
            control={control}
            disabled={isGroupByDisabled}
            id="getEventStats.group_by.type"
            isClearable
            name="getEventStats.group_by.type"
            options={timeGroupingTypeOptions.raw}
          />
        </SettingsItemWrapper>

        <SettingsItemWrapper
          contentClassName={st.SettingsItem__Content}
          data-tooltip-content={(!isIntervalTypeSelected && t('form.clustering.tooltip.interval')) as unknown as string}
          disabled={!isIntervalTypeSelected}
          title={t('form.clustering.count')}
          titleClassName={st.SettingsItem__Title}
        >
          <Control.Input
            {...register(
              'getEventStats.group_by.count',
              {
                validate: validate.integer(),
                min: validate.gte(1),
                required: groupByTypeValue === timeGroupingTypeOptions.interval.value && validate.required(),
              },
            )}
            disabled={!isIntervalTypeSelected}
            errors={errors}
            id="getEventStats.group_by.count"
          />
        </SettingsItemWrapper>

        <SettingsItemWrapper
          contentClassName={st.SettingsItem__Content}
          data-tooltip-content={(!isIntervalTypeSelected && t('form.clustering.tooltip.interval')) as unknown as string}
          disabled={!isIntervalTypeSelected}
          title={t('form.clustering.dimension')}
          titleClassName={st.SettingsItem__Title}
        >
          <ControllableValueSelect
            control={control}
            disabled={!isIntervalTypeSelected}
            errors={errors}
            id="getEventStats.group_by.timeDimension"
            name="getEventStats.group_by.timeDimension"
            options={timeDimensionOptions.raw}
            rules={{ required: isIntervalTypeSelected && validate.required() }}
          />
        </SettingsItemWrapper>

        <SettingsItemWrapper
          contentClassName={st.SettingsItem__Content}
          data-tooltip-content={(!isFrequencyTypeSelected && t('form.clustering.tooltip.frequency')) as unknown as string}
          disabled={!isFrequencyTypeSelected}
          title={t('form.clustering.frequency')}
          titleClassName={st.SettingsItem__Title}
        >
          <ControllableValueSelect
            control={control}
            disabled={!isFrequencyTypeSelected}
            errors={errors}
            id="getEventStats.group_by.frequency"
            name="getEventStats.group_by.frequency"
            options={frequencyOptions.raw}
            rules={{ required: isFrequencyTypeSelected && validate.required() }}
          />
        </SettingsItemWrapper>
      </div>

      <div className={cn(st.LineDivider, st.MarginTop)} />

      <div className={st.FormTitle}>{t('form.clustering.group_by_field')}</div>
      {fields.map((field, i) => {
        if (i === 0) return null;
        return (
          <div
            className={st.ColorPanel}
            key={field.id}
          >
            <div>
              <SettingsItemWrapper
                contentClassName={st.SettingsItem__Content}
                title={t('form.clustering.field')}
                titleClassName={st.SettingsItem__Title}
              >
                <ControllableValueSelect
                  control={control}
                  errors={errors}
                  id={`getEventStats.targets.${i}.column`}
                  isClearable
                  isOptionDisabled={getIsOptionDisabled(watchedGroupByTargets[i]?.column)}
                  name={`getEventStats.targets.${i}.column`}
                  options={targetForGroupByAggregatorOptions.raw}
                  rules={{ required: validate.required() }}
                />
              </SettingsItemWrapper>
              <div className={st.LineDivider} />
              <div className={st.DeleteButton}>
                <Margin>
                  <Control.Button
                    data-testid="deleteFieldButton"
                    icon={<CrossIcon />}
                    kind="negative"
                    onClick={() => remove(i)}
                    size="small"
                    variant="flat"
                  >
                    {t('btn.remove')}
                  </Control.Button>
                </Margin>
              </div>
            </div>
          </div>
        );
      })}
      <Control.Button
        data-testid="addFieldButton"
        disabled={!!isAddFieldButtonDisabled}
        icon={<PlusIcon />}
        onClick={() => append({ column: '', aggregator: 'group_by' })}
        size="small"
        variant="flat"
      >
        {t('btn.add_field')}
      </Control.Button>
    </React.Fragment>
  );
}
